import { 
  collection, 
  doc, 
  getDocs, 
  updateDoc, 
  setDoc,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

export const migrationService = {
  /**
   * Fix the incorrect orgIds where userId = orgId
   * This migration will:
   * 1. Create proper organization documents
   * 2. Update user documents with correct orgIds
   */
  async fixOrganizationIds(): Promise<void> {
    try {
      console.log('🔧 Starting Organization ID Migration...');
      
      // Define the correct organization structure
      const ECHOMOTION_ORG_ID = 'org_echomotion_001';
      const VANDOORN_ORG_ID = 'org_vandoorn_001';
      
      // Step 1: Create Echomotion Organization if it doesn't exist
      const echomotionOrgRef = doc(db, 'organizations', ECHOMOTION_ORG_ID);
      const echomotionDoc = await getDoc(echomotionOrgRef);
      
      if (!echomotionDoc.exists()) {
        console.log('Creating Echomotion organization document...');
        await setDoc(echomotionOrgRef, {
          id: ECHOMOTION_ORG_ID,
          name: 'Echomotion',
          domain: 'echomotion.de',
          industry: 'Technology',
          size: 'small',
          email: 'info@echomotion.de',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: 'sIvlOu3I2TbIgMkvUgeZcxUBorf1', // Benjamin's ID
          subscription: {
            plan: 'professional',
            status: 'active',
            seats: 10
          }
        });
        console.log('✅ Echomotion organization created');
      }
      
      // Step 2: Create Van Doorn Organization if it doesn't exist
      const vandoornOrgRef = doc(db, 'organizations', VANDOORN_ORG_ID);
      const vandoornDoc = await getDoc(vandoornOrgRef);
      
      if (!vandoornDoc.exists()) {
        console.log('Creating Van Doorn organization document...');
        await setDoc(vandoornOrgRef, {
          id: VANDOORN_ORG_ID,
          name: 'Van Doorn Company',
          domain: 'vandoorn.nl',
          industry: 'Professional Services',
          size: 'small',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: 'u12qDyrPWdbhCjxIQaFMealmJjH2', // Jan's ID
          subscription: {
            plan: 'free',
            status: 'active',
            seats: 5
          }
        });
        console.log('✅ Van Doorn organization created');
      }
      
      // Step 3: Update user orgIds
      const usersToUpdate = [
        {
          userId: 'sIvlOu3I2TbIgMkvUgeZcxUBorf1', // Benjamin
          newOrgId: ECHOMOTION_ORG_ID,
          name: 'Benjamin Lange'
        },
        {
          userId: 'ykC2iYrVOsN9aoCvPn5feehNynf1', // Moritz
          newOrgId: ECHOMOTION_ORG_ID,
          name: 'Moritz Weinert'
        },
        {
          userId: 'u12qDyrPWdbhCjxIQaFMealmJjH2', // Jan
          newOrgId: VANDOORN_ORG_ID,
          name: 'Jan van Doorn'
        }
      ];
      
      for (const userUpdate of usersToUpdate) {
        try {
          const userRef = doc(db, 'users', userUpdate.userId);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const currentOrgId = userDoc.data().orgId;
            
            // Only update if the orgId is incorrect (equals userId)
            if (currentOrgId === userUpdate.userId || currentOrgId !== userUpdate.newOrgId) {
              console.log(`Updating ${userUpdate.name}'s orgId from ${currentOrgId} to ${userUpdate.newOrgId}`);
              await updateDoc(userRef, {
                orgId: userUpdate.newOrgId,
                updatedAt: serverTimestamp()
              });
              console.log(`✅ Updated ${userUpdate.name}'s organization`);
            } else {
              console.log(`✓ ${userUpdate.name} already has correct orgId`);
            }
          }
        } catch (error) {
          console.error(`Failed to update user ${userUpdate.userId}:`, error);
        }
      }
      
      // Step 4: Clean up old incorrect organization documents
      // Delete organization documents that use userId as orgId
      const incorrectOrgIds = [
        'sIvlOu3I2TbIgMkvUgeZcxUBorf1', // Benjamin's userId used as orgId
        'u12qDyrPWdbhCjxIQaFMealmJjH2'  // Jan's userId used as orgId
      ];
      
      for (const incorrectId of incorrectOrgIds) {
        try {
          const orgRef = doc(db, 'organizations', incorrectId);
          const orgDoc = await getDoc(orgRef);
          
          if (orgDoc.exists()) {
            console.log(`Found incorrect organization document with ID ${incorrectId} - keeping for backup`);
            // We don't delete, just log for manual cleanup if needed
          }
        } catch (error) {
          console.log(`No incorrect org document found for ${incorrectId}`);
        }
      }
      
      // Step 5: Migrate all existing data to new orgIds
      console.log('📦 Migrating existing data to new organization IDs...');
      
      // Collections to migrate
      const collectionsToMigrate = [
        'leads',       // Contacts
        'opportunities',
        'tasks',
        'companies',
        'products',
        'posts',
        'customers'
      ];
      
      // Map old orgIds to new orgIds
      const orgIdMapping: Record<string, string> = {
        'sIvlOu3I2TbIgMkvUgeZcxUBorf1': ECHOMOTION_ORG_ID, // Benjamin's userId -> Echomotion
        'u12qDyrPWdbhCjxIQaFMealmJjH2': VANDOORN_ORG_ID,  // Jan's userId -> Van Doorn
        'ykC2iYrVOsN9aoCvPn5feehNynf1': ECHOMOTION_ORG_ID  // Moritz's userId -> Echomotion (just in case)
      };
      
      for (const collectionName of collectionsToMigrate) {
        try {
          console.log(`Migrating ${collectionName}...`);
          const collectionRef = collection(db, collectionName);
          const snapshot = await getDocs(collectionRef);
          
          let updatedCount = 0;
          for (const docSnap of snapshot.docs) {
            const data = docSnap.data();
            const currentOrgId = data.orgId;
            
            // Check if this document needs migration
            if (currentOrgId && orgIdMapping[currentOrgId]) {
              const newOrgId = orgIdMapping[currentOrgId];
              await updateDoc(doc(db, collectionName, docSnap.id), {
                orgId: newOrgId,
                updatedAt: serverTimestamp()
              });
              updatedCount++;
            } else if (!currentOrgId) {
              // If no orgId, assign to Echomotion by default
              await updateDoc(doc(db, collectionName, docSnap.id), {
                orgId: ECHOMOTION_ORG_ID,
                updatedAt: serverTimestamp()
              });
              updatedCount++;
            }
          }
          
          if (updatedCount > 0) {
            console.log(`✅ Updated ${updatedCount} ${collectionName} documents`);
          } else {
            console.log(`✓ No ${collectionName} documents needed updating`);
          }
        } catch (error) {
          console.error(`Failed to migrate ${collectionName}:`, error);
          // Continue with other collections even if one fails
        }
      }
      
      console.log('🎉 Organization ID Migration Complete!');
      console.log('');
      console.log('New Organization Structure:');
      console.log('- Echomotion (org_echomotion_001): Benjamin & Moritz');
      console.log('- Van Doorn (org_vandoorn_001): Jan');
      console.log('');
      console.log('All data has been migrated to the new organization IDs');
      
      // Set a flag to prevent running migration again
      localStorage.setItem('orgIdMigrationComplete', 'true');
      
    } catch (error) {
      console.error('❌ Migration failed:', error);
      throw error;
    }
  },
  
  /**
   * Migrate all data for a specific collection
   */
  async migrateCollectionData(
    collectionName: string,
    orgIdMapping: Record<string, string>
  ): Promise<{ success: number; failed: number; skipped: number }> {
    const stats = { success: 0, failed: 0, skipped: 0 };
    
    try {
      const collectionRef = collection(db, collectionName);
      const snapshot = await getDocs(collectionRef);
      
      console.log(`Found ${snapshot.size} documents in ${collectionName}`);
      
      for (const docSnap of snapshot.docs) {
        try {
          const data = docSnap.data();
          const currentOrgId = data.orgId;
          
          // Determine the new orgId
          let newOrgId: string | null = null;
          
          if (currentOrgId && orgIdMapping[currentOrgId]) {
            // Map old orgId to new one
            newOrgId = orgIdMapping[currentOrgId];
          } else if (!currentOrgId || currentOrgId === '') {
            // No orgId set, assign to Echomotion by default
            newOrgId = 'org_echomotion_001';
          } else if (currentOrgId === 'org_echomotion_001' || currentOrgId === 'org_vandoorn_001') {
            // Already has correct orgId
            stats.skipped++;
            continue;
          }
          
          if (newOrgId) {
            await updateDoc(doc(db, collectionName, docSnap.id), {
              orgId: newOrgId,
              updatedAt: serverTimestamp()
            });
            stats.success++;
            console.log(`✓ Updated ${collectionName}/${docSnap.id} to orgId: ${newOrgId}`);
          } else {
            stats.skipped++;
          }
        } catch (error) {
          console.error(`Failed to update document ${docSnap.id}:`, error);
          stats.failed++;
        }
      }
    } catch (error) {
      console.error(`Failed to access collection ${collectionName}:`, error);
    }
    
    return stats;
  },
  
  /**
   * Comprehensive data migration to correct orgIds
   */
  async migrateAllDataToCorrectOrgs(): Promise<void> {
    console.log('🚀 Starting comprehensive data migration...');
    
    const ECHOMOTION_ORG_ID = 'org_echomotion_001';
    const VANDOORN_ORG_ID = 'org_vandoorn_001';
    
    // Map old incorrect orgIds to new correct ones
    const orgIdMapping: Record<string, string> = {
      // Benjamin's userId was incorrectly used as orgId
      'sIvlOu3I2TbIgMkvUgeZcxUBorf1': ECHOMOTION_ORG_ID,
      // Jan's userId was incorrectly used as orgId  
      'u12qDyrPWdbhCjxIQaFMealmJjH2': VANDOORN_ORG_ID,
      // Moritz already had Benjamin's userId as orgId, map to Echomotion
      'ykC2iYrVOsN9aoCvPn5feehNynf1': ECHOMOTION_ORG_ID,
      // Any empty or missing orgId defaults to Echomotion
      '': ECHOMOTION_ORG_ID
    };
    
    // All collections that need migration
    const collectionsToMigrate = [
      { name: 'leads', description: 'Contacts/Leads' },
      { name: 'opportunities', description: 'Opportunities' },
      { name: 'tasks', description: 'Tasks' },
      { name: 'companies', description: 'Companies' },
      { name: 'products', description: 'Products' },
      { name: 'posts', description: 'Stream Posts' },
      { name: 'customers', description: 'Customers' },
      { name: 'activities', description: 'Activities' },
      { name: 'emails', description: 'Email History' }
    ];
    
    const migrationReport: Record<string, any> = {};
    
    for (const { name, description } of collectionsToMigrate) {
      console.log(`\n📦 Migrating ${description} (${name})...`);
      const stats = await this.migrateCollectionData(name, orgIdMapping);
      migrationReport[name] = stats;
      
      if (stats.success > 0) {
        console.log(`✅ ${description}: ${stats.success} migrated, ${stats.skipped} skipped, ${stats.failed} failed`);
      } else if (stats.skipped > 0) {
        console.log(`✓ ${description}: All ${stats.skipped} documents already correct`);
      } else {
        console.log(`ℹ️ ${description}: No documents found or accessible`);
      }
    }
    
    // Print migration report
    console.log('\n📊 Migration Report:');
    console.log('====================');
    let totalMigrated = 0;
    let totalSkipped = 0;
    let totalFailed = 0;
    
    for (const [collection, stats] of Object.entries(migrationReport)) {
      if (stats.success > 0 || stats.failed > 0 || stats.skipped > 0) {
        console.log(`${collection}: ✅ ${stats.success} | ⏭️ ${stats.skipped} | ❌ ${stats.failed}`);
        totalMigrated += stats.success;
        totalSkipped += stats.skipped;
        totalFailed += stats.failed;
      }
    }
    
    console.log('--------------------');
    console.log(`Total: ✅ ${totalMigrated} migrated | ⏭️ ${totalSkipped} skipped | ❌ ${totalFailed} failed`);
    console.log('\n✨ Data migration complete!');
    
    // Mark data migration as complete
    localStorage.setItem('dataOrgIdMigrationComplete', 'true');
  },
  
  /**
   * Check if migration is needed
   */
  async isMigrationNeeded(): Promise<boolean> {
    // Check localStorage flag first
    if (localStorage.getItem('orgIdMigrationComplete') === 'true') {
      return false;
    }
    
    try {
      // Check if any user has userId = orgId
      const usersSnapshot = await getDocs(collection(db, 'users'));
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        if (userData.orgId === userDoc.id) {
          console.log(`Migration needed: User ${userDoc.id} has orgId equal to userId`);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error checking migration status:', error);
      return false;
    }
  },
  
  /**
   * Run migration if needed
   */
  async runMigrationIfNeeded(): Promise<void> {
    const needed = await this.isMigrationNeeded();
    if (needed) {
      console.log('🔄 Organization ID migration needed, running...');
      await this.fixOrganizationIds();
    } else {
      console.log('✅ Organization IDs are correct, no migration needed');
    }
    
    // Also check if data migration is needed
    if (!localStorage.getItem('dataOrgIdMigrationComplete')) {
      console.log('📦 Data migration needed, running...');
      await this.migrateAllDataToCorrectOrgs();
    } else {
      console.log('✅ Data already migrated to correct organizations');
    }
  },
  
  /**
   * Force migrate ALL posts to Echomotion org
   */
  async migrateAllPostsToEchomotion(): Promise<void> {
    console.log('📝 Migrating ALL posts to org_echomotion_001...');
    
    const ECHOMOTION_ORG_ID = 'org_echomotion_001';
    
    try {
      const postsRef = collection(db, 'posts');
      const snapshot = await getDocs(postsRef);
      
      console.log(`Found ${snapshot.size} posts to migrate`);
      
      let migrated = 0;
      let alreadyCorrect = 0;
      let failed = 0;
      
      for (const docSnap of snapshot.docs) {
        try {
          const data = docSnap.data();
          const currentOrgId = data.orgId;
          
          if (currentOrgId !== ECHOMOTION_ORG_ID) {
            // Update to Echomotion org
            await updateDoc(doc(db, 'posts', docSnap.id), {
              orgId: ECHOMOTION_ORG_ID,
              updatedAt: serverTimestamp()
            });
            migrated++;
            console.log(`✓ Post ${docSnap.id} migrated to Echomotion`);
          } else {
            alreadyCorrect++;
          }
        } catch (error) {
          console.error(`Failed to migrate post ${docSnap.id}:`, error);
          failed++;
        }
      }
      
      console.log('\n📊 Posts Migration Summary:');
      console.log(`✅ Migrated: ${migrated}`);
      console.log(`✓ Already correct: ${alreadyCorrect}`);
      console.log(`❌ Failed: ${failed}`);
      console.log(`Total posts: ${snapshot.size}`);
      
      localStorage.setItem('postsOrgIdMigrationComplete', 'true');
    } catch (error) {
      console.error('Failed to migrate posts:', error);
      throw error;
    }
  },
  
  /**
   * Run complete migration (both user orgIds and data)
   */
  async runCompleteMigration(): Promise<void> {
    console.log('🚀 Running complete migration...');
    
    // Step 1: Fix user organization IDs
    console.log('\n📌 Step 1: Fixing user organization IDs...');
    await this.fixOrganizationIds();
    
    // Step 2: Migrate all data to new organization IDs
    console.log('\n📌 Step 2: Migrating all data to correct organizations...');
    await this.migrateAllDataToCorrectOrgs();
    
    // Step 3: Force all posts to Echomotion
    console.log('\n📌 Step 3: Ensuring all posts are in Echomotion...');
    await this.migrateAllPostsToEchomotion();
    
    console.log('\n🎉 Complete migration finished successfully!');
    console.log('All users and data now have correct organization IDs.');
    console.log('Echomotion: org_echomotion_001');
    console.log('Van Doorn: org_vandoorn_001');
  }
};
